import {
    ComparisonChartRow,
    CompanySizeSeniorityRow,
    PositionExperienceRow,
} from '../types';
import { COMPANY_SIZE_ORDER } from '../data/constants';
import { formatPercent, formatSalary } from './formatters';

export const experienceOrder = (experience: string) => {
    const match = experience.match(/\d+/);
    return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
};

export const shortExperience = (experience: string) =>
    experience
        .replace(' Yıl ve üzeri', '+')
        .replace(' Yıl', '')
        .replace(/\s*-\s*/g, '–');

export const sortExperienceRows = (rows: PositionExperienceRow[]) =>
    [...rows].sort(
        (a, b) =>
            experienceOrder(a.experience) - experienceOrder(b.experience)
    );

export const getPositionTotals = (rows: PositionExperienceRow[]) => {
    const totals = new Map<string, number>();

    rows.forEach((row) => {
        totals.set(
            row.position,
            (totals.get(row.position) ?? 0) + row.sampleCount
        );
    });

    return Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
};

export const getPositionOptions = (rows: PositionExperienceRow[]) =>
    getPositionTotals(rows)
        .map(([position]) => position)
        .sort((a, b) => a.localeCompare(b, 'tr'));

export const getPreferredPositions = (rows: PositionExperienceRow[]) =>
    getPositionTotals(rows).map(([position]) => position);

export const buildExperienceInsight = (
    rows: PositionExperienceRow[],
    position: string
) => {
    const ordered = sortExperienceRows(rows);

    if (ordered.length === 0) {
        return `${position} için deneyim kırılımında yeterli veri bulunmuyor.`;
    }

    if (ordered.length === 1) {
        return `${position} için yalnızca ${ordered[0].experience} deneyim aralığında veri bulunuyor.`;
    }

    const first = ordered[0];
    const last = ordered[ordered.length - 1];
    const change = ((last.medianSalary - first.medianSalary) /
        first.medianSalary) *
        100;
    const increases = ordered.slice(1).filter(
        (row, index) => row.medianSalary >= ordered[index].medianSalary
    ).length;
    const mostlyRising = increases >= Math.ceil((ordered.length - 1) / 2);
    const jumps = ordered.slice(1).map((row, index) => ({
        from: ordered[index],
        to: row,
        amount: row.medianSalary - ordered[index].medianSalary,
    }));
    const largestJump = jumps.sort((a, b) => b.amount - a.amount)[0];

    const trend =
        change >= 0
            ? `${position} medyanı, ${first.experience} ile ${last.experience} arasında ${formatPercent(
                  change
              )} artıyor ve ${
                  mostlyRising ? 'genel olarak yükselen' : 'dalgalı'
              } bir seyir izliyor.`
            : `${position} medyanı deneyim aralıkları arasında dalgalanıyor; ilk ve son kırılım arasında ${formatPercent(
                  change
              )} düşüş görülüyor.`;

    if (largestJump.amount <= 0) {
        return trend;
    }

    return `${trend} En belirgin artış ${largestJump.from.experience} ile ${
        largestJump.to.experience
    } arasında (${formatSalary(
        largestJump.from.medianSalary
    )} → ${formatSalary(largestJump.to.medianSalary)}).`;
};

export const sortCompanySizeRows = (
    rows: CompanySizeSeniorityRow[]
) =>
    [...rows].sort(
        (a, b) =>
            COMPANY_SIZE_ORDER.indexOf(
                a.companySize as (typeof COMPANY_SIZE_ORDER)[number]
            ) -
            COMPANY_SIZE_ORDER.indexOf(
                b.companySize as (typeof COMPANY_SIZE_ORDER)[number]
            )
    );

export const buildCompanyInsight = (
    rows: CompanySizeSeniorityRow[],
    seniority: string
) => {
    if (rows.length === 0) {
        return `${seniority} seviyesi için şirket büyüklüğü kırılımında veri bulunmuyor.`;
    }

    const highest = [...rows].sort(
        (a, b) => b.medianSalary - a.medianSalary
    )[0];
    const lowest = [...rows].sort(
        (a, b) => a.medianSalary - b.medianSalary
    )[0];
    const difference = highest.medianSalary - lowest.medianSalary;

    return `${seniority} seviyesinde en yüksek medyan maaş ${
        highest.companySize
    } ölçeğindeki şirketlerde (${formatSalary(
        highest.medianSalary
    )}). En düşük şirket ölçeği medyanıyla fark ${formatSalary(difference)}.`;
};

export const buildComparisonRows = (
    rows: PositionExperienceRow[],
    positionA: string,
    positionB: string
): ComparisonChartRow[] => {
    const rowsA = rows.filter((row) => row.position === positionA);
    const rowsB = rows.filter((row) => row.position === positionB);
    const experiences = Array.from(
        new Set([
            ...rowsA.map((row) => row.experience),
            ...rowsB.map((row) => row.experience),
        ])
    ).sort((a, b) => experienceOrder(a) - experienceOrder(b));

    return experiences.map((experience) => {
        const rowA = rowsA.find((row) => row.experience === experience);
        const rowB = rowsB.find((row) => row.experience === experience);

        return {
            experience,
            experienceOrder: experienceOrder(experience),
            salaryA: rowA?.medianSalary ?? null,
            salaryB: rowB?.medianSalary ?? null,
            sampleA: rowA?.sampleCount ?? null,
            sampleB: rowB?.sampleCount ?? null,
        };
    });
};
